import { Injectable } from '@angular/core';
import { EdgeDefinition } from 'cytoscape';

import {
    Direction,
    GenericSynopticNodeData,
    GenericSynopticNodeDataDefinition,
    NodeType,
} from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import { SynopticGraphBuilderService } from '@connect-services/client/services/synoptic/graph-builder/synoptic-graph-builder.service';
import { GenericSynopticItem } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class GenericSynopticGraphBuilderService extends SynopticGraphBuilderService<GenericSynopticItem> {
    protected override buildEdge(item: GenericSynopticItem): EdgeDefinition {
        const data = {
            dataId: item.edge, // real element id
            label: this.getEdgeLabel(item),
            source: item.from,
            target: item.to,
            typeCode: 'edge',
            uuid: item.edge_uuid,
        };

        const classes = ['edges', this.getElementColorCssClass(item.edge_color)];

        return { classes, data };
    }

    protected override buildNode(item: GenericSynopticItem, suffix: Direction): GenericSynopticNodeDataDefinition {
        const data: GenericSynopticNodeData = {
            id: item[suffix],
            label: item[`${suffix}_type`],
            text_1: item[`${suffix}_text_1`],
            text_2: item[`${suffix}_text_2`],
            text_3: item[`${suffix}_text_3`],
            text_4: item[`${suffix}_text_4`],
            text_5: item[`${suffix}_text_5`],
            text_6: item[`${suffix}_text_6`],
            text_7: item[`${suffix}_text_7`],
            text_8: item[`${suffix}_text_8`],
            title: this.formatTitle(item, suffix),
            type: item[`${suffix}_type`],
            typeCode: item[`${suffix}_type_code`],
            uuid: item[`${suffix}_uuid`],
        } as GenericSynopticNodeData;

        const classes: string[] = this.getNodeClasses(item[`${suffix}_color`]);

        const logicalTypeClass = item[`${suffix}_shape`] === 'RECTANGLE' ? NodeType.NRO : NodeType.SRO;
        classes.push(logicalTypeClass);
        if (item[`${suffix}_type_code`] !== 'virtual') {
            classes.push('is_not_virtual');
        }

        return { classes, data };
    }

    protected override getRenderNodeLabels(
        element: cytoscape.CoreData,
        corpus: string,
        x: number,
        y: number,
    ): { corpus: string; y: number } {
        Object.entries(element.data())
            .filter((keyValue: [string, unknown]) => keyValue[0].startsWith('text_'))
            .forEach((keyValue: [string, unknown]) => {
                if (typeof keyValue[1] !== 'string' || !keyValue[1].length) {
                    return;
                }

                corpus += `<text x="${x}" y="${y}" font-family="Verdana" font-size="12" fill="blue" text-anchor="middle">${keyValue[1]}</text>`;
                y += 20;
            });

        return { corpus, y };
    }

    private formatTitle(item: GenericSynopticItem, suffix: Direction): string {
        let title = item[`${suffix}_type`];

        if (item[`${suffix}_sub_type`]) {
            title += ` - ${item[`${suffix}_sub_type`]}`;
        }

        if (item[`${suffix}_identifiant`]) {
            title += `\n${item[`${suffix}_identifiant`]}`;
        }

        return title;
    }

    private getEdgeLabel(row: GenericSynopticItem): string {
        const lines = [row.edge_identifiant];

        lines.push(
            ...Object.entries(row)
                .filter((keyValue: [string, string]) => keyValue[0].startsWith('edge_text'))
                .map((keyValue: [string, string]) => keyValue[1]),
        );

        return lines.join('\n');
    }

    private getNodeClasses(color: string): string[] {
        return ['nodes', this.getElementColorCssClass(color ?? '')];
    }
}
